import React, {useEffect, useRef, useState} from 'react';
import {useParams, Navigate} from 'react-router-dom';
import {useDisclosure} from '@mantine/hooks';
import {Title, Container, Text, Loader, Modal, Button, Accordion, Group, Select} from '@mantine/core';
import {IconChartBar, IconFileText, IconSettings} from '@tabler/icons-react';
import API_URL from '../../config';
import api from "../../services/api";
import {PowerBIEmbed} from 'powerbi-client-react';
import {models} from 'powerbi-client';
import "../../styles/DemoApp.css";

const ReportViewer = () => {
    const {id} = useParams();
    const [opened, {open, close}] = useDisclosure(false);
    const [reportInfo, setReportInfo] = useState(null);
    const [report, setReport] = useState(null);
    const [reportModal, setReportModal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [embedConfig, setEmbedConfig] = useState(null);
    const [pagesGrouped, setPagesGrouped] = useState({});
    const [selectedPage, setSelectedPage] = useState(null);
    const [selectedPageModal, setSelectedPageModal] = useState(null);
    const [hideMenuPagesGrouped, setHideMenuPagesGrouped] = useState(true);
    const [startReport, setStartReport] = useState(false);
    const [dataReportLoaded, setDataReportLoaded] = useState(false);
    const reportRef = useRef(null);

    const handlePageChange = (pageName) => {
        if (report) {
            report.getPages()
                .then(pages => {
                    const pageToNavigate = pages.find(p => p.name === pageName);
                    if (pageToNavigate) {
                        pageToNavigate.setActive();
                        setSelectedPage(pageName); // pour garder le Select synchro
                        console.log(`Navigué sur la page : ${pageName}`);
                    }
                });
        }
    };
    const handlePageChangeModal = (pageName) => {
        if (report) {
            reportModal.getPages()
                .then(pages => {
                    const pageToNavigate = pages.find(p => p.name === pageName);
                    if (pageToNavigate) {
                        pageToNavigate.setActive();
                        setSelectedPageModal(pageName); // pour garder le Select synchro
                        console.log(`Navigué sur la page : ${pageName}`);
                    }
                });
        }
    };

    const toggleHideMenuPagesGrouped = () => {
        setHideMenuPagesGrouped(prev => !prev);
    };

    const waitForCapacityReady = async () => {
        let attempts = 0;
        const maxAttempts = 10;

        while (attempts < maxAttempts) {
            const statusResp = await api.get(`${API_URL}/powerbi/capacity/status`);
            const state = statusResp.data.capacity_state;
            console.log("Polling capacity state:", state);
            if (state === "Succeeded") {
                return true;
            }
            await new Promise((resolve) => setTimeout(resolve, 5000))
            attempts++;
        }

        return false; // timeout
    };
    const fetchReportData = async () => {
        try {
            setStartReport(false);
            setDataReportLoaded(false);
            setReport(null)
            const response = await api.get(`${API_URL}/powerbi/report/${id}`);
            const data = response.data;
            const embed_info = data.embed_info;
            const capacity_state = data.capacity_state;
            const setConfigEmbed = {
                type: "report",
                id: embed_info.reportConfig[0].reportId,
                embedUrl: embed_info.reportConfig[0].embedUrl,
                accessToken: embed_info.accessToken,
                tokenType: models.TokenType.Embed,
                settings: {
                    panes: {
                        filters: {
                            expanded: false,
                            visible: false,
                        },
                    },
                    navContentPaneEnabled: false,
                },
            }
            setReportInfo(data.report);
            console.log('capacity_state', capacity_state)
            if (capacity_state !== 'Succeeded') {
                console.log("Capacité pas prête, attente du redémarrage...");
                const isReady = await waitForCapacityReady();
                if (isReady) {
                    console.log("Capacité prée, redemarrage...");
                    await new Promise((resolve) => setTimeout(resolve, 5000));
                    fetchReportData();
                    console.log("Capacité prée, redémarrer...");
                }
            } else {
                setEmbedConfig(setConfigEmbed);
            }
        } catch (error) {
            console.error("Erreur lors de la récupération du token:", error);
            setError("Erreur lors du chargement du rapport.");
        } finally {
            setLoading(false);
        }
    };
    const refreshReportData = () => {
        setEmbedConfig(null)
        fetchReportData();
    }
    useEffect(() => {
        fetchReportData();
    }, [id]);

    useEffect(() => {
        console.log('report', report)
        if (report != null) {
            // console.log('report not null', report.getPages())
            report.getPages()
                .then(pages => {
                    console.log("Pages récupérées :", pages);
                    if (pages.length > 0) {
                        setSelectedPage(pages[0].name); // sélectionne 1ère page
                    }
                })
                .catch(error => {
                    console.error("Erreur lors de la récupération des pages:", error);
                });
        }
    }, [report]);

    useEffect(() => {
        console.log('pagesGrouped', pagesGrouped)
    }, [pagesGrouped]);
    useEffect(() => {
        console.log('##### - startReport', startReport)
        console.log('##### - dataReportLoaded', dataReportLoaded)
        if (startReport && !dataReportLoaded) {
            const timer = setTimeout(() => {
                console.log('⏱️ 5 secondes écoulées, on exécute la commande...');
                refreshReportData();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [startReport, dataReportLoaded]);

    const mapPageToIcon = (page) => {
        const name = page.displayName.toLowerCase();
        if (name.includes('dashboard')) return <IconChartBar size={18}/>;
        if (name.includes('rapport') || name.includes('détail')) return <IconFileText size={18}/>;
        if (name.includes('paramètre') || name.includes('config')) return <IconSettings size={18}/>;
        return <IconFileText size={18}/>;
    };

    const openFullscreen = () => {
        const elem = document.querySelector('.report-style-class iframe');
        if (elem.requestFullscreen) {
            elem.requestFullscreen();
        }
    }
    const isTechnicalPage = (page) => {
        const name = page.displayName.toLowerCase();
        return name.startsWith('_') || name.includes('technique') || name.includes('config');
    };

    const groupPagesBySection = (pages) => {
        const sections = {
            Dashboards: [],
            Rapports: [],
            Autres: [],
            Hidden: [],
        };
        pages.forEach(page => {
            const name = page.displayName.toLowerCase();
            if (name.includes('dashboard')) {
                sections.Dashboards.push(page);
            } else if (name.includes('rapport') || name.includes('détail')) {
                sections.Rapports.push(page);
            } else if (name.includes('old') || name.includes('bulle')) {
                sections.Hidden.push(page);
            } else {
                sections.Autres.push(page);
            }
        });
        return sections;
    };
    const pageGetVisuals = async (pages, reportInstance) => {
        for (const page of pages) {
            const visuals = await page.getVisuals();
            console.log('pageGetVisuals start')
            setDataReportLoaded(true);
            for (const visual of visuals) {
                console.log('visual', page.displayName.toLowerCase(), visual.title, visual.type)
                await reportInstance.updateSettings({
                    visualSettings: {
                        visualHeaders: [
                            {
                                // settings: {
                                //     visible: false
                                // },
                                settings: {
                                    exportData: true,
                                    seeData: true,
                                    sort: true,
                                    spotlight: true,
                                    drill: true,
                                    resetVisual: true,
                                    insights: true
                                },
                                selector: {
                                    $schema: "http://powerbi.com/product/schema#visualSelector",
                                    visualName: visual.name
                                }
                            }
                        ]
                    }
                });
            }
        }
    };

    const eventHandlersMap = new Map([
        [
            "loaded",
            async function () {
                console.log("Report has loaded");
                console.log('report loaded', reportRef.current);
                console.log('embedConfig', embedConfig);
                const reportInstance = reportRef.current;
                if (reportInstance) {
                    try {
                        const pages = await reportInstance.getPages();
                        const filteredPages = pages.filter(page => !isTechnicalPage(page));
                        const grouped = groupPagesBySection(filteredPages);
                        setPagesGrouped(grouped);
                        setStartReport(true);
                        pageGetVisuals(pages, reportInstance)
                    } catch (error) {
                        console.error("Erreur lors de la récupération des pages:", error);
                    }
                }
            }
        ],
        ["rendered", function () {
            console.log("Report has rendered");

        }],
        ["error", function (event) {
            if (event) {
                console.error(event.detail);
            }
        }]
    ]);

    if (!id) {
        return <Navigate to="/404" replace/>;
    }

    if (loading) {
        return (
            <Container style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
                <Loader size="lg"/>
            </Container>
        );
    }

    if (error) {
        return (
            <Container style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
                <Text color="red" size="lg">{error}</Text>
            </Container>
        );
    }

    return (
        <Container fluid style={{
            padding: 0,
            margin: 0,
            height: '100vh',
            width: '100vw',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Title order={1} align="center" mt="md">
                {reportInfo ? `${reportInfo.name} : ${id}` : `Rapport : ${id}`}
            </Title>
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "20px 0"
            }}>
                <div style={{display: "flex", gap: "20px"}}>
                    <Button onClick={open} style={{width: 200}}>
                        Mode Plein écran
                    </Button>

                </div>
                {pagesGrouped.Autres?.length > 0 && (
                    <Select
                        label="Changer de page"
                        placeholder="Sélectionne une page"
                        data={pagesGrouped.Autres.map(page => ({value: page.name, label: page.displayName}))}
                        value={selectedPage}
                        onChange={(pageName) => handlePageChange(pageName)}
                        style={{width: 300, margin: "20px auto"}}
                    />
                )}
                {/*{Object.keys(pagesGrouped).length > 0 && (*/}
                {/*    <Button onClick={toggleHideMenuPagesGrouped}>*/}
                {/*        {hideMenuPagesGrouped ? "Afficher le menu" : "Cacher le menu"}*/}
                {/*    </Button>*/}
                {/*)}*/}
            </div>
            {embedConfig ? (
                <>
                    <PowerBIEmbed
                        embedConfig={embedConfig}
                        eventHandlers={eventHandlersMap}
                        cssClassName={"report-style-class"}
                        getEmbeddedComponent={(embedObject) => {
                            console.log(`Embedded object of type "${embedObject.embedtype}" received`);
                            setReport(embedObject);
                            reportRef.current = embedObject;
                        }}
                    />
                    <Modal
                        opened={opened}
                        onClose={close}
                        title={report ? `${reportInfo.name} : ${id}` : `Rapport : ${id}`}
                        fullScreen
                        radius={0}
                        transitionProps={{transition: 'fade', duration: 200}}
                    >
                        {pagesGrouped.Autres?.length > 0 && (
                            <Select
                                label="Changer de page"
                                placeholder="Sélectionne une page"
                                data={pagesGrouped.Autres.map(page => ({value: page.name, label: page.displayName}))}
                                value={selectedPageModal}
                                onChange={(pageName) => handlePageChangeModal(pageName)}
                                style={{width: 300, margin: "20px auto"}}
                            />
                        )}
                        <PowerBIEmbed
                            embedConfig={embedConfig}
                            eventHandlers={eventHandlersMap}
                            cssClassName="report-style-class"
                            getEmbeddedComponent={(embedObject) => {
                                console.log('Embedded object:', embedObject);
                                setReportModal(embedObject);
                                reportRef.current = embedObject;
                            }}
                        />
                    </Modal>
                </>
            ) : (
                <p>Chargement du rapport...</p>
            )}
            {!hideMenuPagesGrouped && (<>
                    {
                        Object.keys(pagesGrouped).length > 0 && (
                            <Accordion chevronPosition="left" variant="separated">
                                {Object.entries(pagesGrouped).map(([section, pages]) => (
                                    <Accordion.Item key={section} value={section}>
                                        <Accordion.Control>
                                            <Text fw={700}>{section}</Text>
                                        </Accordion.Control>
                                        <Accordion.Panel>
                                            {pages.map(page => (
                                                <Group key={page.name} onClick={() => report.setPage(page.name)}
                                                       style={{cursor: 'pointer', padding: '5px 0'}}>
                                                    {mapPageToIcon(page)}
                                                    <Text>{page.displayName}</Text>
                                                </Group>
                                            ))}
                                        </Accordion.Panel>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        )
                    }
                </>
            )}

        </Container>
    );
};

export default ReportViewer;

/*
📄 Informations sur le rapport
	•	getPages() → Liste toutes les pages du rapport
	•	getFilters() → Récupère tous les filtres appliqués
	•	getBookmarksManager() → Gérer les signets (bookmarks)
	•	getVisualByName(name) → Récupérer une visualisation spécifique
	•	getActivePage() → Obtenir la page actuellement affichée

🎯 Actions sur le rapport
	•	setPage(pageName) → Changer de page
	•	setFilters(filters) → Appliquer des filtres globaux
	•	updateSettings(settings) → Modifier dynamiquement les options d’affichage (masquer le menu, le logo Power BI, etc.)
	•	refresh() → Recharger les données du rapport (pull data sans recharger la page)

⚙️ Gestion des visuels
	•	exportVisual(name, exportDataType) → Exporter un visuel spécifique (par exemple en CSV, Excel, etc.)
	•	print() → Imprimer directement le rapport

📣 Événements que tu peux écouter
	•	loaded → Quand le rapport est chargé
	•	rendered → Quand le rapport est affiché
	•	pageChanged → Quand l’utilisateur change de page
	•	filtersApplied → Quand des filtres sont appliqués
	•	dataSelected → Quand l’utilisateur clique sur un élément
	•	error → Quand une erreur est levée

 */
